import React, { useEffect, useState } from "react";
import FilterBar from "./FilterBar";
import PaginatedList from "./PaginatedList";
import ActiveFilterItem from "./ActiveFilterItem";
import CatalogueContainerSkeleton from "./skeleton/CatalogueContainerSkeleton";
import { getTags } from "../../api/tags";
import { getCategories } from "../../api/categories";
import { Media } from "../../compilers/media";
import { Tag } from "../../compilers/tag";
import { LocalisedTag } from "../../compilers/LocalisedTag";
import { useTranslation } from "react-i18next";
import { Category } from "../../compilers/category";
import { LearningPathway } from "../../compilers/learningPathway";
import { LocalisedLearningPathway } from "../../compilers/LocalisedLearningPathway";
import { getLearningPathways } from "../../api/learningPathways";

type CatalogueContainerProps = {
  getPaginatedMedia: (language_code: string) => Promise<Media[]>;
  section_type: string;
  filterKey?: string;
};

const Catalogue: React.FC<CatalogueContainerProps> = ({
  getPaginatedMedia,
  section_type,
  filterKey,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [media, setMedia] = useState<Media[]>([]);
  const [allMedia, setAllMedia] = useState<Media[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [textQuery, setTextQuery] = useState<string>("");
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [allLearningPathways, setAllLearningPathways] = useState<
    LearningPathway[]
  >([]);
  const [activeTags, setActiveTags] = useState<Tag[]>([]);
  const [activeLearningPathwayFilters, setActiveLearningPathwayFilters] =
    useState<(LearningPathway | Tag)[]>([]);
  const [activeSourceFilters, setActiveSourceFilters] = useState<string[]>([]);
  const [activePriceFilters, setActivePriceFilters] = useState<string[]>([]);
  const [activeDifficultyFilters, setActiveDifficultyFilters] = useState<
    string[]
  >([]);
  const [activeTypeFilters, setActiveTypeFilters] = useState<string[]>([]);
  const [orderBy, setOrderBy] = useState<string>("");
  const [filterDropdownExpanded, setFilterDropdownExpanded] =
    useState<boolean>(false);
  const { t, i18n } = useTranslation();

  const toggleFilterDropdownExpanded = () => {
    setFilterDropdownExpanded(!filterDropdownExpanded);
  };

  const getArticlesPage = async () => {
    setLoading(true);
    await getPaginatedMedia(i18n.language).then((response: Media[]) => {
      // check if response is empty
      if (response.length > 1) {
        setLoading(false);
      }
      setMedia(response);
      setAllMedia(response);
    });
  };

  useEffect(() => {
    getTags(i18n.language).then((response: Tag[]) => {
      setAllTags(response);
    });
    getCategories(i18n.language).then((response: Category[]) => {
      setAllCategories(response);
    });
    getLearningPathways().then((response: LearningPathway[]) => {
      if (filterKey && filterKey.length) {
        response.forEach((learningPathway: LearningPathway) => {
          const localisedLearningPathway =
            learningPathway.data.localised_data.find(
              (item) => item.language === i18n.language
            );
          if (
            localisedLearningPathway &&
            filterKey === localisedLearningPathway.title
          ) {
            setActiveLearningPathwayFilters([learningPathway]);
          }
        });
      }

      setAllLearningPathways(response);
    });
  }, [i18n.language]);

  useEffect(() => {
    getArticlesPage();
  }, [i18n.language]);

  /**
   * STATE MANIPULATION LOGIC
   * All functions which are handlers to alter the active filters and the pagination.
   **/
  const nextPage = () => {
    setCurrentPage(currentPage + 1);
  };

  const previousPage = () => {
    setCurrentPage(currentPage - 1);
  };

  // TEXT QUERY MANIPULATION FUNCTIONS
  const clearTextQuery = (clearText: string) => {
    setTextQuery(clearText);
  };

  const searchChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.value) {
      setTextQuery("");
    } else {
      setTextQuery(event.target.value.toLowerCase());
    }
  };

  // TAG FILTER MANIPULATION FUNCTIONS
  const removeTagFromActive = (id: string) => {
    setActiveTags([...activeTags.filter((item) => item.id !== id)]);
  };

  const removeAllTagFilters = () => {
    setActiveTags([]);
  };

  const addActiveTag = (tag: Tag) => {
    if (activeTags.includes(tag)) {
      removeTagFromActive(tag.id);
    } else {
      setActiveTags([...activeTags, tag]);
    }
  };

  // LEARNING PATHWAY MANIPULATION FUNCTIONS
  const removeLearningPathwayFromActive = (id: string) => {
    setActiveLearningPathwayFilters([
      ...activeLearningPathwayFilters.filter((item) => item.id !== id),
    ]);
  };

  const removeAllLearningPathwayFilters = () => {
    setActiveLearningPathwayFilters([]);
  };

  const toggleLearningPathwayFilter = (
    learningPathway: Tag | LearningPathway
  ) => {
    // if the learningPathway is selected is in the current selection
    // remove it
    if (activeLearningPathwayFilters.includes(learningPathway)) {
      setActiveLearningPathwayFilters([
        ...activeLearningPathwayFilters.filter(
          (item) => item !== learningPathway
        ),
      ]);
    } else {
      // if it's not in the active selection, add it
      setActiveLearningPathwayFilters([
        ...activeLearningPathwayFilters,
        learningPathway,
      ]);
    }
    // then reset the current pagination page to 1 so we go back to start of the list
    setCurrentPage(1);
  };

  // DIFFICULTY MANIPULATION FUNCTIONS
  const removeDifficultyFromActive = (difficultyString: string) => {
    setActiveDifficultyFilters([
      ...activeDifficultyFilters.filter((item) => item !== difficultyString),
    ]);
  };

  const removeAllDifficultyFilters = () => {
    setActiveDifficultyFilters([]);
  };

  const toggleActiveDifficultyLevelFilter = (xpFilter: string) => {
    if (activeDifficultyFilters.includes(xpFilter)) {
      setActiveDifficultyFilters([
        ...activeDifficultyFilters.filter((item: string) => item !== xpFilter),
      ]);
    } else {
      setActiveDifficultyFilters([...activeDifficultyFilters, xpFilter]);
    }
  };

  // TYPE MANIPULATION FUNCTION
  const removeTypeFromActive = (type: string) => {
    setActiveTypeFilters([
      ...activeTypeFilters.filter((item) => item !== type),
    ]);
  };

  const removeAllTypeFilters = () => {
    setActiveTypeFilters([]);
  };

  const toggleActiveTypeFilter = (typeFilter: string) => {
    if (activeTypeFilters.includes(typeFilter)) {
      setActiveTypeFilters([
        ...activeTypeFilters.filter((item: string) => item !== typeFilter),
      ]);
    } else {
      setActiveTypeFilters([...activeTypeFilters, typeFilter]);
    }
  };

  // SOURCE MANIPULATION FUNCTION
  const removeSourceFromActive = (type: string) => {
    setActiveSourceFilters([
      ...activeSourceFilters.filter((item) => item !== type),
    ]);
  };

  const removeAllSourceFilters = () => {
    setActiveSourceFilters([]);
  };

  const toggleActiveSourceFilter = (sourceFilter: string) => {
    if (activeSourceFilters.includes(sourceFilter)) {
      setActiveSourceFilters([
        ...activeSourceFilters.filter((item: string) => item !== sourceFilter),
      ]);
    } else {
      setActiveSourceFilters([...activeSourceFilters, sourceFilter]);
    }
  };

  // PRICE MANIPULATION FUNCTIONS
  const togglePriceFilter = (priceFilter: string) => {
    if (activePriceFilters.includes(priceFilter)) {
      setActivePriceFilters([
        ...activePriceFilters.filter((item: string) => item !== priceFilter),
      ]);
    } else {
      setActivePriceFilters([...activePriceFilters, priceFilter]);
    }
  };

  /**
   * FILTER FUNCTIONS
   * These functions take the all media array as an argument and apply a filter to that array
   */
  // category filter
  const filterOnCategory = (
    media: Media[],
    selectedCategoryFilters: string[]
  ): Media[] => {
    if (selectedCategoryFilters.length < 1) {
      return media;
    }
    return media.filter((item: Media) =>
      selectedCategoryFilters.includes(item.data.category)
    );
  };

  // filter the provided media array on the difficulty of the item
  const filterOnDifficulty = (
    media: Media[],
    difficulty: string[]
  ): Media[] => {
    if (difficulty.length < 1) {
      return media;
    }
    return media.filter((item: Media) =>
      difficulty.includes(item.data.difficulty)
    );
  };

  const filterOnType = (media: Media[], activeTypes: string[]): Media[] => {
    if (activeTypes.length < 1) {
      return media;
    }
    return media.filter((item: Media) => activeTypes.includes(item.data.type));
  };

  const filterOnTags = (media: Media[], localisedTags: string[]): Media[] => {
    if (localisedTags.length < 1) {
      return media;
    }
    return media.filter((item: Media) =>
      localisedTags.every((tag) => item.data.tags.includes(tag))
    );
  };

  // learning pathway filter function
  const filterOnLearningPathway = (
    media: Media[],
    localisedLPs: string[]
  ): Media[] => {
    if (localisedLPs.length < 1) {
      return media;
    }
    return media.filter((item: Media) =>
      localisedLPs.some((lp) => item.data.learningPathways.includes(lp))
    );
  };

  // filter the CAFE media based on string.includes in the title, blurb and the body
  const filterOnArticleText = (media: Media[], textQuery: string): Media[] => {
    if (textQuery.length < 1) {
      return media;
    }
    return media.filter((media: Media) => {
      if (typeof media.data.body === "string") {
        return (
          media.data.title.toLowerCase().includes(textQuery) ||
          media.data.blurb.toLowerCase().includes(textQuery)
        );
      }
      return media;
    });
  };

  // filter the ACADEMY media based on string.includes for the title, blurb and the Chapter Title & Chapter Body
  const filterOnCourseText = (media: Media[], textQuery: string): Media[] => {
    if (textQuery.length < 1) {
      return media;
    }
    return media.filter((mediaItem: Media) => {
      if (typeof mediaItem.data.body !== "string") {
        // if the course title or blurb include the textQuery, it should be returned
        if (
          mediaItem.data.title.toLowerCase().includes(textQuery) ||
          mediaItem.data.blurb.toLowerCase().includes(textQuery)
        ) {
          return true;
        }
      }
    });
  };

  // order the media based on published date
  const orderMediaByPublishedDate = (
    media: Media[],
    orderByString: string
  ): Media[] => {
    if (orderByString === "desc") {
      return media
        .sort((a: Media, b: Media) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          return new Date(b.data.publishDate) - new Date(a.data.publishDate);
        })
        .slice(0);
    } else if (orderByString === "asc") {
      return media
        .sort((a: Media, b: Media) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          return new Date(a.data.publishDate) - new Date(b.data.publishDate);
        })
        .slice(0);
    }
    return media;
  };

  useEffect(() => {
    // get a list of all the localised activeTags as a string
    // eg: ["Crypto", "NFT"]
    const localisedTags: string[] = [];
    if (activeTags.length > 0) {
      activeTags.forEach((activeTag: Tag) => {
        if (activeTag.data.localised_data.length > 0) {
          activeTag.data.localised_data.forEach(
            (localisedTag: LocalisedTag) => {
              if (localisedTag.language === i18n.language) {
                localisedTags.push(localisedTag.title);
              }
            }
          );
        }
      });
    }

    // create an array of the localised learning pathway as a string
    // eg: ["Blockchain for Everyone", "Blockchain for Creators & Collectors"]
    const localisedLPs: string[] = [];
    if (activeLearningPathwayFilters.length > 0) {
      activeLearningPathwayFilters.forEach(
        (activeLP: Tag | LearningPathway) => {
          if (activeLP.data.localised_data.length > 0) {
            activeLP.data.localised_data.forEach(
              (localisedLP: LocalisedTag | LocalisedLearningPathway) => {
                if (localisedLP.language === i18n.language) {
                  localisedLPs.push(localisedLP.title);
                }
              }
            );
          }
        }
      );
    }

    // declare the filterMedia array
    let filterMedia: Media[] = allMedia;

    // apply the filters
    filterMedia = filterOnCategory(filterMedia, activeSourceFilters);
    filterMedia = filterOnDifficulty(filterMedia, activeDifficultyFilters);
    filterMedia = filterOnType(filterMedia, activeTypeFilters);

    // apply the array level filters, ie: Tags, Learning Pathways.
    filterMedia = filterOnTags(filterMedia, localisedTags);
    filterMedia = filterOnLearningPathway(filterMedia, localisedLPs);

    // apply the textQuery filter for the cafe articles
    if (section_type === "cafe") {
      filterMedia = filterOnArticleText(filterMedia, textQuery);
    } else if (section_type === "academy") {
      // apply the textQuery filter for the academy courses
      filterMedia = filterOnCourseText(filterMedia, textQuery);
    }

    // the slice here is necessary because: sort does inplace array sorting (modifies the array itself).
    // In order to force the component to re-render, you need to make a copy before sorting
    filterMedia = orderMediaByPublishedDate(filterMedia, orderBy);

    setMedia(filterMedia);
    setCurrentPage(1);
  }, [
    textQuery,
    orderBy,
    activeTags,
    activeLearningPathwayFilters,
    activePriceFilters,
    activeSourceFilters,
    activeTypeFilters,
    activeDifficultyFilters,
  ]);

  /**
   * CLICKABLE PILLS FUNCTIONS
   * Each function returns some clickable elements to interact with the active filters
   * */

  // This function clears all of the active filters
  const handleClearAll = () => {
    clearTextQuery("");
    removeAllTagFilters();
    removeAllLearningPathwayFilters();
    removeAllDifficultyFilters();
    removeAllTypeFilters();
    removeAllSourceFilters();
  };

  // returns a clickable string which invokes the handleClearAll logic
  const getClearAll = () => {
    if (
      activeTags.length ||
      textQuery.length ||
      activeLearningPathwayFilters.length ||
      activeDifficultyFilters.length ||
      activeTypeFilters.length ||
      activeSourceFilters.length
    ) {
      return (
        <p className="tag clear-all-text" onClick={handleClearAll}>
          {t("catalogue-filter.clear")}{" "}
        </p>
      );
    }
  };

  const getSearchQueryPreview = () => {
    if (textQuery.length > 0) {
      return (
        <ActiveFilterItem
          key="search-query-preview"
          quoteTitle={true}
          itemId=""
          title={textQuery}
          removeFilter={clearTextQuery}
        />
      );
    }
  };

  // for each active learning pathway filter, we return a list of ActiveFilterItems
  const getActiveLearningPathwayFilters = () => {
    return activeLearningPathwayFilters.map((learningPathway) => {
      let localisedLpTitle = "";
      const localisedLearningPathway = learningPathway.data.localised_data.find(
        (item) => item.language === i18n.language
      );
      if (localisedLearningPathway) {
        localisedLpTitle = localisedLearningPathway.title;
      }
      return (
        <ActiveFilterItem
          key={learningPathway.id}
          itemId={learningPathway.id}
          title={localisedLpTitle}
          removeFilter={removeLearningPathwayFromActive}
        />
      );
    });
  };

  // for each active tag filter, we return a list of ActiveFilterItems
  const getActiveTagFilters = () => {
    return activeTags.map((activeTag) => {
      let localisedTagTitle = "";
      const localisedTag = activeTag.data.localised_data.find(
        (item) => item.language === i18n.language
      );
      if (localisedTag) {
        localisedTagTitle = localisedTag.title;
      }
      return (
        <ActiveFilterItem
          key={activeTag.id}
          itemId={activeTag.id}
          title={localisedTagTitle}
          removeFilter={removeTagFromActive}
        />
      );
    });
  };

  const getActiveDifficultyFilters = () => {
    return activeDifficultyFilters.map((difficulty: string) => {
      return (
        <ActiveFilterItem
          title={difficulty}
          itemId={difficulty}
          removeFilter={removeDifficultyFromActive}
        />
      );
    });
  };

  const getActiveTypeFilters = () => {
    return activeTypeFilters.map((type: string) => {
      return (
        <ActiveFilterItem
          title={type}
          itemId={type}
          removeFilter={removeTypeFromActive}
        />
      );
    });
  };

  const getActiveSourceFilters = () => {
    return activeSourceFilters.map((source: string) => (
      <ActiveFilterItem
        title={source}
        itemId={source}
        removeFilter={removeSourceFromActive}
      />
    ));
  };

  // returns the inner paginated list
  // if still loading, returns a skeleton
  // if filters result in empty data set, we return the No Results h2
  const innerCatalogueSkeleton = () => {
    if (loading) {
      return <CatalogueContainerSkeleton />;
    }

    if (!loading && media && media.length < 1) {
      return (
        <>
          <h2
            style={{
              marginTop: "8rem",
            }}
          >
            {t("catalogue-page.no-results")}
          </h2>
        </>
      );
    }

    if (media.length > 0) {
      return (
        <PaginatedList
          articles={media}
          currentPage={currentPage}
          nextPage={nextPage}
          previousPage={previousPage}
          isFilterDropdownExpanded={filterDropdownExpanded}
          section_type={section_type}
        />
      );
    }
  };

  return (
    <div className="row position-relative justify-content-center">
      <FilterBar
        textQuery={textQuery}
        setQueryString={searchChangeHandler}
        allTags={allTags}
        allCategories={allCategories}
        activeTags={activeTags}
        addActiveTag={addActiveTag}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
        setFilterDropdownExpanded={toggleFilterDropdownExpanded}
        addPriceFilter={togglePriceFilter}
        activePriceFilters={activePriceFilters}
        addDifficultyLevelFilter={toggleActiveDifficultyLevelFilter}
        activeDifficultyLevelFilters={activeDifficultyFilters}
        allLearningPathways={allLearningPathways}
        addLearningPathwayFilter={toggleLearningPathwayFilter}
        activeLearningPathwayFilters={activeLearningPathwayFilters}
        addSourceFilter={toggleActiveSourceFilter}
        activeSourceFilters={activeSourceFilters}
        addTypeFilter={toggleActiveTypeFilter}
        activeTypeFilters={activeTypeFilters}
        section_type={section_type}
      />
      <div
        className={`d-flex flex-wrap align-items-center active-tags-container ${
          filterDropdownExpanded ? "active-tags" : "active-tags remove-margin"
        }`}
      >
        {getSearchQueryPreview()}
        {getActiveLearningPathwayFilters()}
        {getActiveDifficultyFilters()}
        {getActiveTypeFilters()}
        {getActiveSourceFilters()}
        {getActiveTagFilters()}
        {getClearAll()}
      </div>
      {innerCatalogueSkeleton()}
    </div>
  );
};

export default Catalogue;
